import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Button,
  Form,
  Input,
  DatePicker,
  Select,
  Row,
  Col,
  Divider,
  AutoComplete,
  ConfigProvider,
  InputNumber,
  Radio,
  Typography,
  Image,
  message,
} from "antd";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import "dayjs/locale/pt-br";
import localeData from "dayjs/plugin/localeData";
import weekday from "dayjs/plugin/weekday";
import weekOfYear from "dayjs/plugin/weekOfYear";
import weekYear from "dayjs/plugin/weekYear";
import customParseFormat from "dayjs/plugin/customParseFormat";

import ptBR from "antd/es/locale/pt_BR";
import moment from "moment";
import classes from "../AddActivities.module.scss";
import { GAME_API } from "../../../../constants/api";
import { EditOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons";
import Header from "../../../../Components/Header/Header";
import ButtonActions from "../../../../Components/Buttons/ButtonActions/ButtonActions";

const AddActivitiesPage1 = ({
  nextPage,
  gameData = {},
  updatedGameDate,
  errorField = {},
  gamesMapField,
  pathValid,
  gameImage,
  pdfAudio,
}) => {
  const { Link } = Typography;
  const history = useHistory();
  dayjs.locale("pt-br");
  dayjs.extend(customParseFormat);
  dayjs.extend(advancedFormat);
  dayjs.extend(weekday);
  dayjs.extend(localeData);
  dayjs.extend(weekOfYear);
  dayjs.extend(weekYear);

  const [form] = Form.useForm();
  const fileInputRef = useRef(null);
  const pathValue = Form.useWatch("path", form);
  const rawTypeValue = Form.useWatch("type", form);
  const typeValue = Array.isArray(rawTypeValue)
    ? rawTypeValue[0]
    : rawTypeValue;

  const [isEditingFavorites, setIsEditingFavorites] = useState(false);
  const [isEditingLikes, setIsEditingLikes] = useState(false);
  const [tempFavorites, setTempFavorites] = useState(gameData.favorites || 0);
  const [tempLikes, setTempLikes] = useState(gameData.likes || 0);

  const handleSaveFavorites = () => {
    form.setFieldValue("favorites", tempFavorites);
    updatedGameDate("favorites", tempFavorites);
    setIsEditingFavorites(false);
  };

  const handleCancelFavorites = () => {
    setTempFavorites(gameData.favorites || 0);
    setIsEditingFavorites(false);
  };
  const handleSaveLikes = () => {
    setIsEditingLikes(false);
    form.setFieldValue("likes", tempLikes);
    updatedGameDate("likes", tempLikes);
  };

  const handleCancelLikes = () => {
    setTempLikes(gameData.likes || 0);
    setIsEditingLikes(false);
  };

  useEffect(() => {
    if (!gameData) return;

    const toFormValues = (data) => {
      const copy = { ...data };
      copy.release_date = data.release_date
        ? dayjs(data.release_date, ["DD/MM/YYYY", "YYYY-MM-DD", "DD-MM-YYYY"])
        : null;
      return copy;
    };

    form.setFieldsValue(toFormValues(gameData));
  }, [gameData, form]);

  // onValuesChange: atualiza o estado pai via `updatedGameDate` quando o formulário muda
  const onValuesChange = (changedValues, allValues) => {
    Object.keys(changedValues).forEach((key) => {
      if (key === "id") return; // 🚫 nunca atualiza o id

      const value = changedValues[key];
      if (key === "release_date") {
        updatedGameDate(key, value ? dayjs(value).format("YYYY-MM-DD") : null);
      } else {
        updatedGameDate(key, value);
      }
    });
  };
  const normalizeDateValue = (v) => {
    if (!v) return null;
    if (dayjs.isDayjs(v)) return v;
    if (typeof v === "string" || v instanceof Date) {
      return dayjs(v, ["DD/MM/YYYY", "YYYY-MM-DD", "DD-MM-YYYY"], true);
    }
    return null;
  };

  const handleNextPage = () => nextPage();

  const handleCancel = () => {
    history.push("/admin/atividades");
  };

  // IMPORT: abre o seletor de arquivo oculto
  const handleImportClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  // Ao carregar o arquivo JSON, aplicamos no formulário e também chamamos updatedGameDate para cada campo
  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = null; // limpa para permitir reimportar o mesmo arquivo
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const parsed = JSON.parse(evt.target.result);
        // converte release_date para moment para o form
        const formValues = { ...parsed };
        if (parsed.release_date) {
          formValues.release_date = dayjs(parsed.release_date, "YYYY-MM-DD");
        } else {
          formValues.release_date = null;
        }
        if (gameData.id) {
          formValues.id = gameData.id;
        }

        // seta o form (visual)
        form.setFieldsValue(formValues);

        // atualiza o pai (dados reais) — converte moment de volta para string quando necessário
        Object.keys(parsed).forEach((key) => {
          if (key === "id") return;
          if (key === "favorites") return;
          if (key === "likes") return;
          if (key === "release_date") {
            updatedGameDate(key, parsed[key] || null);
          } else {
            updatedGameDate(key, parsed[key]);
          }
        });

        message.success("Informações importadas com sucesso");
      } catch (err) {
        console.error(err);
        message.error("Arquivo inválido ou formato JSON incorreto");
      }
    };
    reader.readAsText(file);
  };

  // EXPORT: pega os valores atuais do formulário (misturando com gameData) e baixa como JSON
  const handleExport = () => {
    try {
      const { favorites, likes, ...rest } = form.getFieldsValue();
      const formValues = rest;
      const normalized = { ...gameData, ...formValues };
      // garantir que a data esteja em string DD-MM-YYYY
      if (normalized.release_date && dayjs.isDayjs(normalized.release_date)) {
        normalized.release_date = normalized.release_date.format("DD-MM-YYYY");
      }

      const blob = new Blob([JSON.stringify(normalized, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `game_${normalized.id || "data"}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      message.success("Informações exportadas com sucesso");
    } catch (err) {
      console.error(err);
      message.error("Erro ao exportar");
    }
  };

  // Helpers para opções (usadas diretamente nas props abaixo)
  const mapToOptions = (arr) =>
    arr ? arr.map((v) => ({ label: v, value: v })) : [];
  const optionsAbility = mapToOptions(gamesMapField?.["main_ability_tag"]);
  const optionsLanguage = mapToOptions(gamesMapField?.["language"]);
  const optionsType = mapToOptions(gamesMapField?.["type"]);
  const optionsEngine = mapToOptions(gamesMapField?.["engine"]);
  const optionsBehaviorTags = mapToOptions(gamesMapField?.["behavior_tags"]);
  const optionsTags = mapToOptions(gamesMapField?.["optionsTags"]);
  const optionsSimilarActivities = gamesMapField?.["id"]
    ? gamesMapField["id"].map((game) => ({
        label: `Jogo: ${game.name}`,
        value: game.id,
      }))
    : [];
  const optionsDifficulty = mapToOptions(gamesMapField?.["difficulty"]);
  const optionsTime = gamesMapField?.["time"]
    ? gamesMapField["time"].map((t) => ({
        label: t.charAt(0).toUpperCase() + t.slice(1),
        value: t,
      }))
    : [];

  const labelStyle = { color: "#3C3C3C", fontSize: "16px", fontWeight: "500" };
  const inputStyle = { width: "100%" };

  const isPathValid = pathValid === true;
  const isPathInvalid =
    pathValid === false || (errorField && errorField["path"]?.length > 0);
  const errorMessage =
    (errorField && errorField["path"]) ||
    (pathValid === false
      ? " Caminho inválido. Suba primeiro o jogo no GitHub."
      : "");

  useEffect(() => {
    if (pathValue !== undefined) {
      updatedGameDate("path", pathValue);
    }
  }, [pathValue]);

  return (
    <>
      <Header
        text="Cadastrar/Editar  Atividade"
        style={{ marginBottom: "20px" }}
      />

      {/* botões importar/exportar */}
      <div style={{ marginBottom: 16, display: "flex", gap: "10px" }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <ButtonActions
          variant="outline"
          style={{ marginRight: 8 }}
          onClick={handleImportClick}
          text="Importar Informações"
        />
        <ButtonActions
          variant="outline"
          style={{ marginRight: 8 }}
          onClick={handleExport}
          text="Exportar Informações"
        />
      </div>

      <Form
        form={form}
        style={{ maxWidth: 1200 }}
        size="large"
        layout="vertical"
        labelCol={{ span: 12 }}
        wrapperCol={{ span: 16 }}
        onValuesChange={onValuesChange}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Form.Item
              label={<span style={labelStyle}>Id Único</span>}
              name="id"
              hasFeedback={!!(errorField && errorField["id"])}
              validateStatus={
                errorField && errorField["id"]?.length > 0 ? "error" : ""
              }
              help={errorField && errorField["id"]}
            >
              <InputNumber
                placeholder="Id único"
                style={inputStyle}
                value={gameData.id}
                disabled
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item
              label={<span style={labelStyle}>Nome do Jogo</span>}
              name="name"
              hasFeedback={!!(errorField && errorField["name"])}
              validateStatus={
                errorField && errorField["name"]?.length > 0 ? "error" : ""
              }
              help={errorField && errorField["name"]}
            >
              <Input
                placeholder="Nome do jogo"
                style={inputStyle}
                addonAfter={
                  gameImage ? (
                    <Image
                      width={44}
                      height={24}
                      src={gameImage}
                      alt={`Imagem do jogo: ${gameData["name"]}`}
                    />
                  ) : null
                }
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item
              label={<span style={labelStyle}>Tipo</span>}
              name="type"
              hasFeedback={!!(errorField && errorField["type"])}
              validateStatus={
                errorField && errorField["type"]?.length > 0 ? "error" : ""
              }
              help={errorField && errorField["type"]}
            >
              <AutoComplete
                placeholder="Selecione um tipo"
                mode="tags"
                tokenSeparators={[","]}
                style={inputStyle}
                options={optionsType}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Form.Item
              label={<span style={labelStyle}>Habilidades</span>}
              name="main_ability_tag"
              hasFeedback={!!(errorField && errorField["main_ability_tag"])}
              validateStatus={
                errorField && errorField["main_ability_tag"]?.length > 0
                  ? "error"
                  : ""
              }
              help={errorField && errorField["main_ability_tag"]}
            >
              <Select
                placeholder="Habilidades estimuladas"
                mode="tags"
                tokenSeparators={[","]}
                style={inputStyle}
                options={optionsAbility}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item
              label={<span style={labelStyle}>Behavior tags</span>}
              name="behavior_tags"
              hasFeedback={!!(errorField && errorField["behavior_tags"])}
              validateStatus={
                errorField && errorField["behavior_tags"]?.length > 0
                  ? "error"
                  : ""
              }
              help={errorField && errorField["behavior_tags"]}
            >
              <Select
                placeholder="Tags de acompanhamento"
                mode="tags"
                tokenSeparators={[","]}
                options={optionsBehaviorTags}
                style={inputStyle}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item label={<span style={labelStyle}>Tags</span>} name="tags">
              <Select
                placeholder="Tags de filtragem"
                mode="tags"
                tokenSeparators={[","]}
                options={optionsTags}
                style={inputStyle}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Form.Item
              label={<span style={labelStyle}>Visibilidade</span>}
              name="visible"
              hasFeedback={!!(errorField && errorField["visible"])}
              validateStatus={
                errorField && errorField["visible"]?.length > 0 ? "error" : ""
              }
              help={errorField && errorField["visible"]}
            >
              <Radio.Group>
                <Row gutter={80}>
                  <Col xs={12} sm={8}>
                    <Radio value={true} style={{ lineHeight: "32px" }}>
                      Visível
                    </Radio>
                  </Col>
                  <Col xs={12} sm={8}>
                    <Radio value={false} style={{ lineHeight: "32px" }}>
                      Oculto
                    </Radio>
                  </Col>
                </Row>
              </Radio.Group>
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item
              name="favorites"
              label={<span style={{ fontWeight: 500 }}>Favoritado por</span>}
            >
              {isEditingFavorites ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <InputNumber
                    min={0}
                    value={tempFavorites}
                    onChange={setTempFavorites}
                    style={{ width: 100 }}
                  />
                  <span>Só edite esse valor em último caso se necessário</span>
                  <Button
                    type="text"
                    icon={<CheckOutlined />}
                    onClick={handleSaveFavorites}
                    style={{ color: "green" }}
                  />
                  <Button
                    type="text"
                    icon={<CloseOutlined />}
                    onClick={handleCancelFavorites}
                    style={{ color: "red" }}
                  />
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Typography.Text>
                    {form.getFieldValue("favorites") || 0} usuário(s)
                  </Typography.Text>
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => setIsEditingFavorites(true)}
                  />
                </div>
              )}
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item
              name="likes"
              label={<span style={labelStyle}>Curtido por</span>}
            >
              {isEditingLikes ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <InputNumber
                    value={tempLikes}
                    onChange={setTempLikes}
                    style={inputStyle}
                  />
                  <span>Só edite esse valor em último caso se necessário</span>
                  <Button
                    type="text"
                    icon={<CheckOutlined />}
                    onClick={handleSaveLikes}
                    style={{ color: "green" }}
                  />
                  <Button
                    type="text"
                    icon={<CloseOutlined />}
                    onClick={handleCancelLikes}
                    style={{ color: "red" }}
                  />
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Typography.Text>
                    {gameData.likes ? gameData.likes : 0} usuário(s)
                  </Typography.Text>
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => setIsEditingLikes(true)}
                  />
                </div>
              )}
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ borderWidth: "4px" }} />

        <Row gutter={4}>
          <Col xs={24} sm={8}>
            <Form.Item
              label={<span style={labelStyle}>Atividades Similares</span>}
              name="similar_activities"
              hasFeedback={!!(errorField && errorField["similar_activities"])}
              validateStatus={
                errorField && errorField["similar_activities"]?.length > 0
                  ? "error"
                  : ""
              }
              help={errorField && errorField["similar_activities"]}
            >
              <Select
                mode="tags"
                tokenSeparators={[","]}
                placeholder="Atividades Similares"
                options={optionsSimilarActivities}
                style={inputStyle}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item
              label={<span style={labelStyle}>Dificuldade</span>}
              name="difficulty"
              hasFeedback={!!(errorField && errorField["difficulty"])}
              validateStatus={
                errorField && errorField["difficulty"]?.length > 0
                  ? "error"
                  : ""
              }
              help={errorField && errorField["difficulty"]}
            >
              <AutoComplete
                placeholder="Dificuldades"
                style={inputStyle}
                options={optionsDifficulty}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item
              label={<span style={labelStyle}>Idioma</span>}
              name="language"
              hasFeedback={!!(errorField && errorField["language"])}
              validateStatus={
                errorField && errorField["language"]?.length > 0 ? "error" : ""
              }
              help={errorField && errorField["language"]}
            >
              <AutoComplete
                placeholder="Selecione um idioma"
                style={inputStyle}
                options={optionsLanguage}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item
              label={<span style={labelStyle}>Quantidade de níveis</span>}
              name="qtd_levels"
              hasFeedback={!!(errorField && errorField["qtd_levels"])}
              validateStatus={
                errorField && errorField["qtd_levels"]?.length > 0
                  ? "error"
                  : ""
              }
              help={errorField && errorField["qtd_levels"]}
            >
              <InputNumber
                placeholder="Digite uma quantidade"
                style={inputStyle}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item
              label={<span style={labelStyle}>Data de Lançamento</span>}
              name="release_date"
              hasFeedback={!!(errorField && errorField["release_date"])}
              validateStatus={
                errorField && errorField["release_date"]?.length > 0
                  ? "error"
                  : ""
              }
              help={errorField && errorField["release_date"]}
            >
              <ConfigProvider locale={ptBR}>
                <DatePicker
                  placeholder="00/00/0000"
                  format="DD/MM/YYYY"
                  style={inputStyle}
                  value={normalizeDateValue(form.getFieldValue("release_date"))}
                  onChange={(date) => {
                    const normalized = date ? dayjs(date) : null;
                    form.setFieldValue("release_date", normalized);
                    updatedGameDate(
                      "release_date",
                      normalized ? normalized.format("YYYY-MM-DD") : null,
                    );
                  }}
                />
              </ConfigProvider>
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item
              label={<span style={labelStyle}>Tempo</span>}
              name="time"
              hasFeedback={!!(errorField && errorField["time"])}
              validateStatus={
                errorField && errorField["time"]?.length > 0 ? "error" : ""
              }
              help={errorField && errorField["time"]}
            >
              <AutoComplete
                placeholder="Tempo"
                style={inputStyle}
                options={optionsTime}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item
              label={<span style={labelStyle}>Caminho pasta</span>}
              name="path"
              hasFeedback={
                !!(errorField && errorField["path"]) || pathValid !== null
              }
              validateStatus={isPathInvalid ? "error" : ""}
              help={errorMessage}
            >
              <Input
                placeholder="Caminho pasta"
                value={pathValue || ""}
                style={inputStyle}
                onChange={(e) => {
                  form.setFieldValue("path", e.target.value); // Atualiza o Form
                }}
                addonAfter={
                  isPathValid ? (
                    <CheckOutlined style={{ color: "green" }} />
                  ) : null
                }
              />

              {isPathValid && (
                <Row>
                  <Col span={24}>
                    <>
                      <Link
                        className={classes.TestPathLink}
                        href={
                          GAME_API +
                          "/" +
                          pathValue +
                          (typeValue === "Audio" ? "/audio.mp3" : "/index.html")
                        }
                        target="_blank"
                      >
                        Testar caminho
                      </Link>
                      {gameImage && (
                        <div>
                          <Image
                            className={classes.ImageGame}
                            src={gameImage}
                            alt={`Imagem do jogo: ${gameData["name"]}`}
                          />
                        </div>
                      )}
                      {pdfAudio && (
                        <div>
                          <Link
                            className={classes.TestPathLink}
                            href={pdfAudio}
                            target="_blank"
                          >
                            Gabarito
                          </Link>
                        </div>
                      )}
                    </>
                  </Col>
                </Row>
              )}
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item
              label={<span style={labelStyle}>Engine</span>}
              name="engine"
              hasFeedback={!!(errorField && errorField["engine"])}
              validateStatus={
                errorField && errorField["engine"]?.length > 0 ? "error" : ""
              }
              help={errorField && errorField["engine"]}
            >
              <AutoComplete
                placeholder="Selecione um engine"
                mode="tags"
                tokenSeparators={[","]}
                style={inputStyle}
                options={optionsEngine}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item
              label={<span style={labelStyle}>Versão</span>}
              name="version"
              hasFeedback={!!(errorField && errorField["version"])}
              validateStatus={
                errorField && errorField["version"]?.length > 0 ? "error" : ""
              }
              help={errorField && errorField["version"]}
            >
              <Input placeholder="Versão do jogo" style={inputStyle} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
          <div className={classes.buttonContainer}>
            <ButtonActions
              variant="outline"
              onClick={handleCancel}
              text="Cancelar"
            />

            <ButtonActions
              variant="primary"
              onClick={handleNextPage}
              text="Continuar"
            />
          </div>
        </Form.Item>
      </Form>
    </>
  );
};

export default AddActivitiesPage1;
